import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { useAtom } from 'jotai';
import { Card, Group, ActionIcon, Text, Textarea, Button, Stack, Box, Loader } from '@mantine/core';
import { IconArrowLeft, IconDeviceFloppy } from '@tabler/icons-react';
import { uiDesignerOpenAtom, uiDesignerCodeAtom } from '@/stores/atoms/uiAtoms';
import { useSwipe } from '@/renderer/utils/gestures';
import { useT } from '@/i18n';
import { wsManager } from '@/utils/wsManager';
import { languageAtom } from '@/stores/atoms/i18nAtoms';
import { buildWsStreamHandler } from '@/renderer/utils/wsStream';
import DesignerCodeEditor from './DesignerCodeEditor';
import { writeRuntimeIframe } from '@/renderer/utils/runtimeUi';
const GameUIDesignerOverlay = () => {
    const t = useT();
    const [open, setOpen] = useAtom(uiDesignerOpenAtom);
    const [draft, setDraft] = React.useState('');
    const [code, setCode] = useAtom(uiDesignerCodeAtom);
    const [currentHTML, setCurrentHTML] = React.useState('');
    const previewRef = React.useRef(null);
    const [lang] = useAtom(languageAtom);
    const [cmd, setCmd] = React.useState('');
    const [isStreaming, setIsStreaming] = React.useState(false);
    const [pending, setPending] = React.useState(false);
    const monoFont = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';
    const writePreview = React.useCallback((html) => {
        writeRuntimeIframe(previewRef.current, html);
    }, []);
    const [phase, setPhase] = React.useState('idle');
    React.useEffect(() => {
        if (open) {
            setPhase('opening');
            const t = window.setTimeout(() => setPhase('open'), 260);
            // Initialize preview from saved code and editor draft
            setDraft(code || '');
            setCurrentHTML(code || '');
            writePreview(code || '');
            // Connect to special adventure for UI designing
            try {
                wsManager.connect({ scriptId: 'ui_designer', language: lang });
            }
            catch { }
            // Stream server responses into code editor (reuse chat protocol)
            wsManager.setHandler(buildWsStreamHandler({
                onStreamText: (text, isStreaming) => {
                    setIsStreaming(isStreaming);
                    setPending(false);
                    if (text)
                        setDraft((prev) => prev + text);
                },
                onFinalText: (text) => {
                    if (typeof text === 'string')
                        setDraft(text);
                    setIsStreaming(false);
                    setPending(false);
                },
                onServerError: (err) => {
                    try {
                        console.error('[UI Designer] server_error', err);
                    }
                    catch { }
                    setPending(false);
                },
            }));
            return () => {
                window.clearTimeout(t);
                try {
                    wsManager.setHandler(null);
                }
                catch { }
                try {
                    wsManager.close();
                }
                catch { }
            };
        }
        if (!open && phase !== 'idle')
            setPhase('idle');
    }, [open, writePreview]);
    const onClose = () => {
        setPhase('closing');
        const t = window.setTimeout(() => setOpen(false), 260);
        try {
            wsManager.setHandler(null);
        }
        catch { }
        try {
            wsManager.close();
        }
        catch { }
        return () => window.clearTimeout(t);
    };
    const onSave = () => {
        // Persist current preview HTML back to ui_code
        setCode(currentHTML || '');
        onClose();
    };
    const onSend = () => {
        const html = draft || '';
        writePreview(html);
        setCurrentHTML(html);
    };
    // Hooks must run unconditionally; compute swipe handlers before any early return
    const swipeToClose = useSwipe({ threshold: 60, onRight: onClose, startEdgeLeftPx: 24 });
    if (!open)
        return null;
    return (_jsx("div", { className: "chat-overlay", children: _jsxs("div", { className: `chat-overlay-panel ${phase}`, ...swipeToClose, children: [_jsx("div", { className: "overlay-top-gap", "aria-hidden": true }), _jsx(Card, { withBorder: true, radius: 0, p: "sm", className: "overlay-header no-safe-top", children: _jsxs(Group, { justify: "space-between", gap: "sm", wrap: "nowrap", children: [_jsxs(Group, { gap: "sm", wrap: "nowrap", children: [_jsx(ActionIcon, { variant: "light", radius: "xl", "aria-label": t('back'), onClick: onClose, children: _jsx(IconArrowLeft, { size: 18 }) }), _jsx(Text, { fw: 800, children: t('uiDesigner.title') })] }), _jsx(Group, { gap: "xs", wrap: "nowrap", children: _jsx(Button, { size: "xs", radius: "xl", leftSection: _jsx(IconDeviceFloppy, { size: 14 }), onClick: onSave, children: t('create.save') }) })] }) }), _jsxs(Stack, { gap: 0, style: { flex: 1, minHeight: 0 }, children: [_jsx(Box, { style: { marginTop: 'var(--mantine-spacing-sm)', flex: '0 0 40%', minHeight: 160, borderBottom: '1px solid var(--app-border)', display: 'flex', alignItems: 'stretch', justifyContent: 'stretch' }, children: _jsx("iframe", { ref: previewRef, style: { width: '100%', height: '100%', border: 'none' }, title: "UI Preview" }) }), _jsx(Box, { p: "xs", style: { borderBottom: '1px solid var(--app-border)', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }, children: _jsxs(Group, { gap: "xs", wrap: "nowrap", justify: "center", children: [_jsx(Button, { size: "sm", radius: "xl", onClick: () => { writePreview(draft || ''); setCurrentHTML(draft || ''); }, children: "\u9884\u89C8" }), isStreaming && _jsx(Text, { size: "xs", c: "dimmed", children: "\u751F\u6210\u4E2D..." })] }) }), _jsxs(Box, { style: { position: 'relative', flex: 1, minHeight: 0 }, children: [_jsx(DesignerCodeEditor, { value: draft, onChange: setDraft }), pending && (_jsx("div", { style: { position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.25)', pointerEvents: 'none', zIndex: 2 }, children: _jsx(Loader, { color: "gray", size: "md" }) }))] }), _jsx(Card, { withBorder: false, radius: 0, p: "xs", style: { position: 'sticky', bottom: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(6px)', borderTop: '1px solid var(--app-border)', marginBottom: 'calc(8px + max(env(safe-area-inset-bottom, 0px), var(--sab, 0px)))' }, children: _jsxs(Group, { align: "flex-end", gap: "xs", wrap: "nowrap", children: [_jsx(Textarea, { value: cmd, onChange: (e) => setCmd(e.currentTarget.value), placeholder: "\u5728\u6B64\u8F93\u5165\u751F\u6210\u6307\u4EE4\uFF0C\u56DE\u8F66\u53D1\u9001", autosize: true, minRows: 1, maxRows: 3, style: { flex: 1 }, onKeyDown: (e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                const text = cmd.trim();
                                                if (!text)
                                                    return;
                                                setCmd('');
                                                try {
                                                    // Clear editor to avoid concatenating previous result with new stream
                                                    setDraft('');
                                                    setPending(true);
                                                    wsManager.sendText(text);
                                                }
                                                catch {
                                                    setPending(false);
                                                }
                                            }
                                        } }), _jsx(Button, { size: "xs", radius: "xl", onClick: () => { const text = cmd.trim(); if (!text)
                                            return; setCmd(''); try {
                                            setDraft('');
                                            setPending(true);
                                            wsManager.sendText(text);
                                        }
                                        catch {
                                            setPending(false);
                                        } }, children: "\u53D1\u9001" })] }) })] })] }) }));
};
export default GameUIDesignerOverlay;
